import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';

/** Viewport width below which the drawer opens as a modal overlay (no squeeze). */
const MODAL_BREAKPOINT = 990;

/**
 * Hard ceiling for the close animation. If `onAnimationEnd` never settles —
 * an interrupted view transition, an in-app WebView that stops compositing,
 * an infinite-iteration animation picked up by `getAnimations()` — the close
 * MUST still complete. Without this the dialog stays in the browser top layer
 * with its ::backdrop swallowing every click, which is what made the page feel
 * frozen. Comfortably longer than --animation-speed (200-400ms).
 */
const CLOSE_ANIMATION_TIMEOUT = 600;

/**
 * A drawer that opens from the right side.
 *
 * On wide viewports (≥ 990px) the drawer squeezes page content alongside it.
 * The panel is a non-modal dialog (`show()`); we install a focus trap via
 * `trapFocus()` so Tab cycles within the drawer, mirroring the modal-mode
 * a11y contract. Focus moves to the close button on open and returns to
 * the trigger on close.
 *
 * On narrow viewports (< 990px) the drawer overlays with a backdrop. The
 * panel is a modal dialog (`showModal()`) — native focus trap, scroll-lock,
 * and ARIA semantics. Same focus-on-close-button + restore-on-close UX.
 *
 * Closing is guaranteed. Every close path funnels through `#release()`, which
 * is idempotent and runs in a `finally` block, so a throw or a hung animation
 * can never leave the drawer open, the body scroll-locked, or `#isClosing`
 * latched true.
 *
 * Dispatches {@link DrawerOpenEvent} and {@link DrawerCloseEvent}.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel - The dialog that owns all visual styles and animation.
 *
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  /** Monotonically increasing counter for z-index stacking of multiple open drawers. */
  static #stackOrder = 0;

  /** @type {boolean} */
  #isClosing = false;

  /** When true, the drawer will reopen after the current close animation finishes. */
  /** @type {{ enter?: 'inline-start' } | false} */
  #deferredOpen = false;

  /** Element to restore focus to when the drawer closes. */
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /**
   * The panel node we most recently locked scroll for. Section Rendering API
   * morphs can swap the dialog node between open and close; unlocking with the
   * *current* ref would leave the old node in the scroll-lock owner set and the
   * page permanently unscrollable. We always unlock the node we locked.
   * @type {Element | null}
   */
  #scrollLockOwner = null;

  /** Listeners bound while the drawer is open. Aborted wholesale on close. */
  /** @type {AbortController | null} */
  #openListeners = null;

  /** @type {MediaQueryList} */
  #modalQuery = window.matchMedia(`(max-width: ${MODAL_BREAKPOINT - 1}px)`);

  /**
   * @returns {boolean} Whether the drawer is currently open.
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  /**
   * Non-throwing panel accessor. Every close path must survive a morph that is
   * mid-flight.
   * @returns {HTMLDialogElement | null}
   */
  get #panel() {
    try {
      const panel = this.refs?.panel;
      // Only trust the ref while it is still the live node. Component#updateRefs
      // THROWS MissingRefError when a required ref is absent, which leaves
      // `this.refs` holding the previous object — so mid-morph `refs.panel` can
      // be a detached dialog. Closing that one would leave the real dialog open
      // in the top layer with a click-blocking backdrop.
      if (panel instanceof HTMLDialogElement && panel.isConnected && this.contains(panel)) return panel;
    } catch {
      /* fall through to a live query */
    }
    return this.querySelector('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#modalQuery.addEventListener('change', this.#onModalBreakpointChange);

    // Sync the static stack counter with any --drawer-stack-order set by the
    // synchronous restore script (which runs before this module loads).
    const restoredOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  /**
   * Restore path: the inline script in theme-drawer.liquid set [open] on
   * <theme-drawer> + <dialog> before this module loaded. The dialog is
   * already visible — we just wire close listeners. We deliberately skip
   * trapFocus and focus moves: the user is loading a fresh page and
   * expects focus on main content, not inside a drawer left over from
   * the previous session.
   */
  #onRestore() {
    const panel = this.#panel;
    if (!panel) return;

    if (this.#modalQuery.matches) {
      this.#lockScroll(panel);
    }

    this.#bindOpenListeners(panel);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unlockScroll();
    this.#modalQuery.removeEventListener('change', this.#onModalBreakpointChange);
    this.#removeEventListeners();
    removeTrapFocus();
    this.#deferredOpen = false;
    this.#isClosing = false;
    ThemeDrawer.releaseStrandedScrollLock();
  }

  /**
   * Binds every listener the drawer needs while open under a single
   * AbortController. Re-binding is safe and idempotent: the previous
   * controller is aborted first, so repeated open/close cycles can never
   * accumulate duplicate listeners.
   *
   * @param {HTMLDialogElement} panel
   */
  #bindOpenListeners(panel) {
    this.#openListeners?.abort();
    this.#openListeners = new AbortController();
    const { signal } = this.#openListeners;

    document.addEventListener('keydown', this.#onKeyDown, { signal });
    panel.addEventListener('click', this.#onBackdropClick, { signal });

    // Outside-click for sidebar (non-modal) mode. There is no ::backdrop at
    // ≥990px, so the panel click handler above is inert there and clicking the
    // page never closed the drawer. Pointerdown on the document covers it.
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, { signal, capture: true });

    // Safety nets. If the dialog is dismissed by anything other than close()
    // — the browser's native Escape handling, a `dialog.close()` from another
    // script, form submission — reconcile our state so [open], the scroll lock
    // and the squeeze class can't be left behind.
    panel.addEventListener('cancel', this.#onDialogCancel, { signal });
    panel.addEventListener('close', this.#onDialogClose, { signal });
  }

  /**
   * Closes the drawer when the user clicks the backdrop (outside the dialog
   * content area). Only meaningful in modal mode — in sidebar mode the
   * dialog has no backdrop, and `#onDocumentPointerDown` handles it instead.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #onBackdropClick = (event) => {
    const panel = this.#panel;
    if (!panel) return;

    if (isClickedOutside(event, panel)) {
      if (this.#hasOpenNestedDialog()) return;

      this.close();
    }
  };

  /**
   * Outside-click handling for sidebar (non-modal) mode.
   *
   * @param {PointerEvent} event
   */
  #onDocumentPointerDown = (event) => {
    if (!this.isOpen || this.#isClosing) return;
    // Modal mode already closes via the ::backdrop click handler.
    if (this.#modalQuery.matches) return;
    if (!this.#isTopmost()) return;
    if (this.#hasOpenNestedDialog()) return;

    const panel = this.#panel;
    const target = event.target;
    if (!panel || !(target instanceof Element)) return;

    if (panel.contains(target)) return;

    // Ignore the controls that open/toggle this drawer, otherwise the cart
    // icon would close the drawer on pointerdown and immediately reopen it on
    // click, producing a visible flicker.
    if (target.closest(`[aria-controls="${this.id}"]`)) return;
    if (target.closest(`[on\\:click*="#${this.id}"]`)) return;

    // Ignore clicks inside any other open dialog or popover layered above us.
    if (target.closest('dialog[open], [popover]:popover-open')) return;

    this.close();
  };

  /**
   * Escape pressed while the dialog has native focus (modal mode). We handle
   * Escape ourselves in `#onKeyDown` so the full teardown runs; suppress the
   * native dismissal so the browser can't close the dialog behind our back.
   *
   * @param {Event} event
   */
  #onDialogCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /**
   * The dialog reported closed. If our own close path didn't drive it, our
   * state is now stale — release it. Guarded by `#isClosing` so the normal
   * path doesn't re-enter.
   */
  #onDialogClose = () => {
    if (this.#isClosing) return;
    if (!this.isOpen) return;
    this.#release();
    this.dispatchEvent(new DrawerCloseEvent());
  };

  /**
   * @returns {boolean} Whether the drawer panel contains an open dialog other than itself.
   */
  #hasOpenNestedDialog() {
    return this.#getOpenNestedDialog() !== null;
  }

  /**
   * @returns {HTMLDialogElement | null} The open dialog nested inside the drawer panel, if any.
   */
  #getOpenNestedDialog() {
    return /** @type {HTMLDialogElement | null} */ (this.#panel?.querySelector('dialog[open]') ?? null);
  }

  /**
   * Switches the dialog between modal and non-modal when the viewport
   * crosses the modal breakpoint while the drawer is open.
   */
  #onModalBreakpointChange = () => {
    if (!this.isOpen) return;

    const panel = this.#panel;
    if (!panel) return;

    const nestedDialog = this.#getOpenNestedDialog();
    const nestedActiveElement =
      nestedDialog?.contains(document.activeElement) && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Close the current dialog mode and immediately reopen in the new mode.
    // No animation — the drawer stays visually in place. Suppress the
    // reconciling `close` handler for the duration of the swap.
    this.#isClosing = true;
    try {
      panel.close();
      removeTrapFocus();

      if (this.#modalQuery.matches) {
        this.#lockScroll(panel);
        panel.showModal();
      } else {
        this.#unlockScroll();
        panel.show();
        trapFocus(panel);
      }
    } catch (error) {
      console.warn('[theme-drawer] Failed to switch drawer mode:', error);
    } finally {
      this.#isClosing = false;
    }

    if (nestedDialog?.open) {
      this.#bringNestedDialogToFront(nestedDialog, nestedActiveElement);
    } else {
      this.#focusInitialElement();
    }

    // Rebind under a fresh controller. The sessionStorage restore path bypasses
    // open(), so listeners may not have been registered yet; aborting first
    // guarantees exactly one of each regardless.
    this.#bindOpenListeners(panel);
  };

  /**
   * Closes the drawer when the user presses Escape.
   * preventDefault() suppresses the native dialog cancel event in modal mode.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    // isComposing: don't close while an Input Method Editor (e.g. Japanese,
    // Chinese, Korean input) consumes Escape to dismiss its candidate list —
    // can happen inside the cart's discount-code field.
    // defaultPrevented: let nested components handle Escape first.
    if (event.isComposing || event.defaultPrevented) return;
    if (!this.isOpen) return;
    if (this.#hasOpenNestedDialog()) return;
    if (!this.#isTopmost()) return;

    event.preventDefault();
    this.close();
  };

  /**
   * Shows the drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction. `'inline-start'` slides left-to-right (used when the chat docks).
   */
  open({ enter } = {}) {
    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }
    if (this.#isClosing) {
      this.#deferredOpen = { enter };
      return;
    }

    const panel = this.#panel;
    if (!panel) return;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);

    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);

    try {
      if (this.#modalQuery.matches) {
        this.#lockScroll(panel);
        if (!panel.open) panel.showModal();
      } else if (!panel.open) {
        panel.show();
        trapFocus(panel);
      }
    } catch (error) {
      // showModal() throws InvalidStateError if the dialog is already open as
      // non-modal. Recover rather than leaving a half-open drawer.
      console.warn('[theme-drawer] Failed to open dialog:', error);
      try {
        panel.close();
        if (this.#modalQuery.matches) panel.showModal();
        else panel.show();
      } catch {
        panel.setAttribute('open', '');
      }
    }

    this.#focusInitialElement();

    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });
    // Belt and braces: if the open animation never settles the class would
    // stick and re-trigger on the next open. Time it out.
    setTimeout(() => panel.classList.remove(openClass), CLOSE_ANIMATION_TIMEOUT);

    this.setAttribute('open', '');
    this.dispatchEvent(new DrawerOpenEvent());

    this.#bindOpenListeners(panel);
  }

  /**
   * Bumps this drawer's z-index to the top of the stack and replays
   * the slide-in animation. Called when open() is invoked on an
   * already-open drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction.
   */
  #bringToFront({ enter } = {}) {
    const panel = this.#panel;
    if (!panel) return;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    // In modal mode, dialogs live in the browser's top layer where z-index
    // is ignored — stacking follows showModal() call order. Re-calling
    // showModal() moves this dialog to the top of the stack.
    if (this.#modalQuery.matches && panel.open) {
      this.#isClosing = true;
      try {
        this.#lockScroll(panel);
        panel.close();
        panel.showModal();
      } catch (error) {
        console.warn('[theme-drawer] Failed to restack drawer:', error);
      } finally {
        this.#isClosing = false;
      }
    }

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);
    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });
    setTimeout(() => panel.classList.remove(openClass), CLOSE_ANIMATION_TIMEOUT);

    // Re-fire so persistDrawerState picks up the new stack order.
    this.dispatchEvent(new DrawerOpenEvent());
  }

  #removeEventListeners() {
    this.#openListeners?.abort();
    this.#openListeners = null;
  }

  /**
   * Adds the scroll lock, remembering which node owns it.
   * @param {Element} owner
   */
  #lockScroll(owner) {
    if (this.#scrollLockOwner && this.#scrollLockOwner !== owner) {
      unlockScroll(this.#scrollLockOwner);
    }
    this.#scrollLockOwner = owner;
    lockScroll(owner);
  }

  /** Releases the scroll lock for whichever node currently owns it. */
  #unlockScroll() {
    const owner = this.#scrollLockOwner;
    this.#scrollLockOwner = null;

    if (owner) unlockScroll(owner);

    // A morph can swap the panel node mid-flight, so also release the live one.
    const panel = this.#panel;
    if (panel && panel !== owner) unlockScroll(panel);
  }

  /**
   * Clears a scroll lock left on <html> when no drawer or modal dialog is
   * actually open. `unlockScroll` only manages owners it knows about; a
   * detached or orphaned owner could otherwise keep the page unscrollable.
   * The MutationObserver inside `syncScrollLock` re-applies the attribute on
   * the next DOM change if a legitimate owner still exists, so this is safe.
   */
  static releaseStrandedScrollLock() {
    if (document.querySelector('theme-drawer[open]')) return;
    if (document.querySelector('dialog:modal')) return;

    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.removeProperty('overflow');
    document.documentElement.style.removeProperty('overflow');
  }

  /**
   * Moves focus to the close button inside the drawer panel.
   * Every theme-drawer renders the close button via theme-drawer-header.
   * If a future drawer omits it, that drawer is responsible for its own focus management.
   */
  #focusInitialElement() {
    const closeButton = /** @type {HTMLElement | null} */ (
      this.#panel?.querySelector('.theme-drawer__close-button') ?? null
    );
    closeButton?.focus();
  }

  /**
   * Reopens a nested modal after the drawer panel changes mode so it remains
   * above the drawer in the browser top layer.
   *
   * @param {HTMLDialogElement} dialog - The nested dialog to restack.
   * @param {HTMLElement | null} focusTarget - Element to refocus inside the nested dialog.
   */
  #bringNestedDialogToFront(dialog, focusTarget) {
    try {
      dialog.close();
      dialog.showModal();
    } catch (error) {
      console.warn('[theme-drawer] Failed to restack nested dialog:', error);
      return;
    }

    if (focusTarget && document.contains(focusTarget) && dialog.contains(focusTarget)) {
      focusTarget.focus();
    }
  }

  /**
   * Races a promise against a timeout so a never-settling animation can't
   * strand the close. Always resolves.
   *
   * @param {Promise<unknown>} promise
   * @param {number} ms
   * @returns {Promise<void>}
   */
  #withTimeout(promise, ms) {
    return new Promise((resolve) => {
      const timer = setTimeout(resolve, ms);
      Promise.resolve(promise)
        .catch(() => {})
        .finally(() => {
          clearTimeout(timer);
          resolve(undefined);
        });
    });
  }

  /**
   * Idempotent teardown. Removes [open], drops every animation class, closes
   * the dialog, releases the scroll lock, clears the stack order and restores
   * focus. Safe to call twice; safe to call when the panel has gone missing.
   */
  #release() {
    const panel = this.#panel;

    this.removeAttribute('open');

    if (panel) {
      panel.classList.remove(
        'theme-drawer__dialog--opening',
        'theme-drawer__dialog--opening-inline-start',
        'theme-drawer__dialog--closing',
        'theme-drawer__dialog-instant'
      );

      try {
        if (panel.open) panel.close();
      } catch (error) {
        console.warn('[theme-drawer] dialog.close() failed:', error);
      }

      // If close() was a no-op for any reason, strip the attribute directly so
      // the dialog cannot keep rendering (and cannot keep eating pointers).
      panel.removeAttribute('open');
      panel.style.removeProperty('pointer-events');
    }

    this.#removeEventListeners();
    removeTrapFocus();
    this.#unlockScroll();
    this.style.removeProperty('--drawer-stack-order');
    this.style.removeProperty('pointer-events');

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
      document.querySelector('.page-wrapper')?.classList.remove('page-wrapper--drawer-open');
    }

    ThemeDrawer.releaseStrandedScrollLock();

    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;
    // A Section Rendering API morph between open and close can replace the
    // trigger node. The JS reference stays valid but the element is detached
    // from the live DOM — and .focus() on a detached node is a silent no-op,
    // so we explicitly check and fall back to a fresh query.
    try {
      if (trigger && document.contains(trigger) && trigger.isConnected) {
        trigger.focus();
      } else {
        /** @type {HTMLElement | null} */ (document.querySelector(`[aria-controls="${this.id}"]`))?.focus();
      }
    } catch (error) {
      console.warn('[theme-drawer] Failed to restore focus:', error);
    }
  }

  /**
   * Slides the drawer out, waits for the animation (with a hard timeout),
   * then releases every piece of open state.
   */
  async #closeDialog() {
    if (!this.isOpen) {
      // Nothing to animate, but state may still be dirty from an interrupted
      // close. Reconcile and bail.
      this.#release();
      return;
    }
    if (this.#isClosing) return;
    this.#isClosing = true;

    try {
      this.#removeEventListeners();
      removeTrapFocus();

      const panel = this.#panel;

      this.removeAttribute('open');
      this.dispatchEvent(new DrawerCloseEvent());

      this.#unlockScroll();

      if (panel?.open) {
        // Cancel any in-progress open animation before starting the close.
        panel.classList.remove('theme-drawer__dialog--opening', 'theme-drawer__dialog--opening-inline-start');
        panel.classList.add('theme-drawer__dialog--closing');

        // Never await this unguarded: an interrupted view transition or an
        // in-app WebView that stops compositing leaves `animation.finished`
        // pending forever, and the dialog would stay in the top layer with a
        // click-blocking ::backdrop. That was the freeze.
        await this.#withTimeout(onAnimationEnd(panel, undefined, { subtree: false }), CLOSE_ANIMATION_TIMEOUT);
      }
    } catch (error) {
      console.warn('[theme-drawer] Error while closing, forcing release:', error);
    } finally {
      // Runs no matter what. This is the guarantee that the X button always
      // works and that #isClosing can never stay latched.
      this.#release();
      this.#isClosing = false;

      // Reconcile: if open() was called during the close animation, reopen now.
      if (this.#deferredOpen) {
        const options = this.#deferredOpen;
        this.#deferredOpen = false;
        this.open(options);
      }
    }
  }

  /**
   * Closes the drawer.
   */
  async close() {
    if (this.#isClosing) {
      // A close is already in progress — cancel any deferred open so the
      // drawer stays closed when the animation finishes.
      this.#deferredOpen = false;
      return;
    }
    await this.#closeDialog();
  }

  /**
   * Toggles the drawer. If the drawer is open but not the topmost in the
   * stack, it is brought to front instead of closed.
   */
  toggle() {
    if (this.isOpen && this.#isTopmost()) {
      return this.close();
    }
    return this.open();
  }

  /**
   * @returns {boolean} Whether this drawer has the highest stack order among all open drawers.
   */
  #isTopmost() {
    const myOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    for (const other of document.querySelectorAll('theme-drawer[open]')) {
      if (other === this) continue;
      const otherOrder = Number(/** @type {HTMLElement} */ (other).style.getPropertyValue('--drawer-stack-order') || 0);
      if (otherOrder > myOrder) return false;
    }
    return true;
  }
}

if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
}

/**
 * Global recovery net. If the tab is restored from bfcache, or a navigation is
 * cancelled mid-close, or any script closes a dialog out from under us, a
 * stranded scroll lock would leave the page unscrollable with no way back.
 * These handlers cost nothing and make that unrecoverable state impossible.
 */
window.addEventListener('pageshow', () => ThemeDrawer.releaseStrandedScrollLock());
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') ThemeDrawer.releaseStrandedScrollLock();
});

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(DrawerOpenEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:close';
}
